/**
 * Mirrors health-hrms-backend/src/internal/patient/constant/patient.ts
 * (PATIENT_FORM_CATALOG + helpers). Keep in sync with the backend catalog.
 */

export const CLINICAL_FORM_STATUS = [
  "pending",
  "send_to_office",
  "to_be_signed",
  "to_be_corrected",
  "completed",
];

export const CLINICAL_FORM_STATUS_LABELS = {
  pending: "Pending",
  send_to_office: "Sent to Office",
  to_be_signed: "To Be Sign",
  to_be_corrected: "To Be Corrected",
  completed: "Completed",
};

export const PATIENT_CLINICAL_FORM_SECTIONS = [
  "admission-documents",
  "physician-orders",
  "plan-of-care",
  "soc-roc-recert",
  "nurse-notes",
  "pt-notes",
  "ot-notes",
  "st-notes",
  "msw-notes",
  "chha-notes",
];

/**
 * Repeating discipline visit-note pattern (Eval/Visit/Sup/Reeval/Missed/Discharge x2).
 *
 * The nurse set is labelled "Nurse" rather than "RN" because RN and LVN both
 * work from it; the `rn_` formType prefix is only an identifier.
 */
function disciplineVisitNoteForms(section, prefix, disciplineLabel) {
  return [
    {
      section,
      formType: `${prefix}_eval_note`,
      formName: `${disciplineLabel} Evaluation Note`,
    },
    {
      section,
      formType: `${prefix}_visit_note`,
      formName: `${disciplineLabel} Visit Note`,
    },
    {
      section,
      formType: `${prefix}_sup_visit`,
      formName: `${disciplineLabel} Supervisory Visit`,
    },
    {
      section,
      formType: `${prefix}_reeval_note`,
      formName: `${disciplineLabel} Re-evaluation Note`,
    },
    {
      section,
      formType: `${prefix}_missed_visit`,
      formName: `${disciplineLabel} Missed Visit`,
    },
    {
      section,
      formType: `${prefix}_discharge_note`,
      formName: `${disciplineLabel} Discharge Note`,
    },
    {
      section,
      formType: `${prefix}_discharge_non_visit`,
      formName: `${disciplineLabel} Discharge (Non-Visit)`,
    },
  ];
}

/**
 * OASIS assessment pattern shared across RN/PT/OT/ST. Every discipline keeps
 * its own copy, so the label is carried through the transfer note too — the
 * section lists all four disciplines side by side.
 */
function socRocRecertForms(prefix, disciplineLabel) {
  return [
    {
      section: "soc-roc-recert",
      group: disciplineLabel,
      formType: `${prefix}_soc_note`,
      formName: `OASIS ${disciplineLabel} Start of Care Note`,
    },
    {
      section: "soc-roc-recert",
      group: disciplineLabel,
      formType: `${prefix}_roc_note`,
      formName: `OASIS ${disciplineLabel} Resumption of Care Note`,
    },
    {
      section: "soc-roc-recert",
      group: disciplineLabel,
      formType: `${prefix}_recert_note`,
      formName: `OASIS ${disciplineLabel} Recertification Note`,
    },
    {
      section: "soc-roc-recert",
      group: disciplineLabel,
      formType: `${prefix}_oasis_discharge_note`,
      formName: `OASIS ${disciplineLabel} Discharge Note`,
    },
    {
      section: "soc-roc-recert",
      group: disciplineLabel,
      formType: `${prefix}_transfer_note`,
      formName: `OASIS ${disciplineLabel} Transfer Note`,
    },
  ];
}

/**
 * Full catalog of patient clinical/admission forms, keyed by formType.
 * Mirrors the backend `patient_clinical_forms` table catalog exactly.
 */
export const PATIENT_FORM_CATALOG = [
  // Admission documents
  {
    section: "admission-documents",
    formType: "consent_form",
    formName: "Patient Consent",
  },
  {
    section: "admission-documents",
    formType: "emergency_category",
    formName: "Emergency Category & Preparedness",
  },
  {
    section: "admission-documents",
    formType: "non_coverage_medicare",
    formName: "Notice of Medicare Non-Coverage (NOMNC)",
  },

  // Physician orders — discipline comes from filled-by / role on the row
  {
    section: "physician-orders",
    formType: "noa_order",
    formName: "NOA Order",
  },
  {
    section: "physician-orders",
    formType: "physician_order",
    formName: "Physician Order",
  },
  {
    section: "physician-orders",
    formType: "supplemental_physician_order",
    formName: "Supplemental Physician Order",
  },
  {
    section: "physician-orders",
    formType: "face_to_face_encounter",
    formName: "Face-to-Face Encounter",
  },

  // Plan of care
  {
    section: "plan-of-care",
    formType: "plan_of_care_note",
    formName: "Plan of Care Note",
  },

  // OASIS SOC / ROC / Recert / Discharge / Transfer — RN, PT, OT, ST
  ...socRocRecertForms("rn", "Nurse"),
  ...socRocRecertForms("pt", "Physical Therapy"),
  ...socRocRecertForms("ot", "Occupational Therapy"),
  ...socRocRecertForms("st", "Speech Therapy"),

  // Discipline visit notes
  ...disciplineVisitNoteForms("nurse-notes", "rn", "Nurse"),
  ...disciplineVisitNoteForms("pt-notes", "pt", "Physical Therapy"),
  ...disciplineVisitNoteForms("ot-notes", "ot", "Occupational Therapy"),
  ...disciplineVisitNoteForms("st-notes", "st", "Speech Therapy"),

  // MSW
  {
    section: "msw-notes",
    formType: "msw_eval_note",
    formName: "MSW Evaluation Note",
  },
  {
    section: "msw-notes",
    formType: "msw_visit_note",
    formName: "MSW Visit Note",
  },
  {
    section: "msw-notes",
    formType: "msw_missed_visit",
    formName: "MSW Missed Visit",
  },

  // CHHA
  {
    section: "chha-notes",
    formType: "chha_plan_of_care_note",
    formName: "CHHA Plan of Care Note",
  },
  {
    section: "chha-notes",
    formType: "chha_visit_note",
    formName: "CHHA Visit Note",
  },
  {
    section: "chha-notes",
    formType: "chha_missed_visit",
    formName: "CHHA Missed Visit",
  },
];

/** All forms belonging to a given section, in catalog order. */
export const getFormsBySection = (section) =>
  PATIENT_FORM_CATALOG.filter((f) => f.section === section);

/**
 * A section's forms split into headed groups, for sections long enough that a
 * flat list is hard to scan. Sections whose entries carry no `group` come back
 * as one unlabelled group, so callers can render both shapes the same way.
 */
export const getFormGroupsBySection = (section) => {
  const groups = [];
  for (const form of getFormsBySection(section)) {
    const label = form.group || null;
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.forms.push(form);
    else groups.push({ label, forms: [form] });
  }
  return groups;
};

/** Catalog metadata (section + display name) for a given formType, or null. */
export const getFormMeta = (formType) =>
  PATIENT_FORM_CATALOG.find((f) => f.formType === formType) || null;

/** Sample default payload used when creating clinical forms without data. */
export const SAMPLE_CLINICAL_FORM_DATA = {
  patientSummary: "",
  clinicalNotes: "",
  findings: "",
  interventions: "",
  goals: "",
  additionalDetails: "",
  clinicianSignature: "",
  formDate: null,
};

/** Human labels for the dynamic sample form field keys. */
export const CLINICAL_FORM_FIELD_LABELS = {
  patientSummary: "Patient Summary",
  clinicalNotes: "Clinical Notes",
  findings: "Findings",
  interventions: "Interventions",
  goals: "Goals",
  additionalDetails: "Additional Details",
  clinicianSignature: "Clinician Signature",
  formDate: "Form Date",
};
